'use client';

import { useId, useRef, useState } from 'react';

/**
 * Choosing a file, on a product where the file is the thing being protected.
 *
 * Two rules shape this control and neither is negotiable. It never renders a
 * preview: what would be in the thumbnail is her intimate material, and a
 * product that exists to stop other people looking at it should not be the
 * fourth thing on the screen doing so. And it says where the bytes go, on the
 * control itself rather than in a policy page, because the question anybody
 * hesitating over this button is asking is "where does this end up".
 *
 * What it does show is the file's name and size, which is what a person needs
 * to be sure they picked the right one.
 */
export function FileField({
  label,
  hint,
  accept,
  maxBytes,
  busy,
  file,
  onChange,
  error,
}: {
  label: string;
  hint?: string;
  /** Accept attribute, e.g. `image/jpeg,image/png`. */
  accept: string;
  /** Refused here as well as by the server, so the message arrives instantly. */
  maxBytes: number;
  busy?: boolean;
  file: File | null;
  onChange: (file: File | null, problem: string | null) => void;
  error?: string | null;
}) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function take(chosen: File | null) {
    if (!chosen) {
      onChange(null, null);
      return;
    }
    if (chosen.size > maxBytes) {
      onChange(null, `That file is ${megabytes(chosen.size)} MB. The limit is ${megabytes(maxBytes)} MB.`);
      return;
    }
    onChange(chosen, null);
  }

  return (
    <div className="fieldrow">
      <label className="label" htmlFor={inputId}>
        {label}
      </label>

      {/*
        A label rather than a div with a click handler. The whole area is then
        the control as far as the keyboard and the screen reader are concerned,
        and there is no second focusable thing pretending to be the first.
      */}
      <label
        htmlFor={inputId}
        className={`filedrop${over ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          if (busy) return;
          take(event.dataTransfer.files?.[0] ?? null);
        }}
      >
        <input
          ref={input}
          id={inputId}
          type="file"
          accept={accept}
          disabled={busy}
          className="filedrop-input"
          onChange={(event) => take(event.target.files?.[0] ?? null)}
        />

        {file ? (
          <span className="filedrop-chosen">
            {/* The same hatched square the library uses, and for the same
                reason: it is the policy drawn as a picture, not a placeholder
                waiting for an image to arrive. */}
            <span className="filedrop-seal" aria-hidden />
            <span className="filedrop-name">
              <strong>{file.name}</strong>
              <span>{megabytes(file.size)} MB</span>
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                if (input.current) input.current.value = '';
                take(null);
              }}
            >
              Choose another
            </button>
          </span>
        ) : (
          <span className="filedrop-prompt">
            <strong>Choose a file</strong>
            <span>or drop one here</span>
          </span>
        )}
      </label>

      {error ? (
        <p className="fieldrow-error">{error}</p>
      ) : hint ? (
        <p className="fieldrow-hint">{hint}</p>
      ) : null}
    </div>
  );
}

function megabytes(bytes: number): number {
  return Math.round((bytes / (1024 * 1024)) * 10) / 10;
}
